import { useEffect, useState } from "react";
import { Copy, Ban, Ticket } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Section } from "./Section";

const EMPTY = { company: "", client_email: "", client_nip: "", client_phone: "", client_address: "", mode: "days", days: "365", expires: "", olt: "", clients: "", phone_interval: "7" };
const ST = { new: ["czeka na użycie", "text-amber-300"], used: ["użyty", "text-emerald-300"], cancelled: ["anulowany", "text-slate-500"], using: ["w trakcie", "text-amber-300"] };
const IN = "bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white outline-none focus:ring-1 focus:ring-cyan-500";

function Field({ label, children }) {
  return <label className="flex flex-col gap-0.5 text-[11px] text-slate-400">{label}{children}</label>;
}

function CodeRow({ c, onCancel }) {
  const [label, cls] = ST[c.status] || [c.status, ""];
  const period = c.mode === "days" ? `${c.days} dni od aktywacji` : c.mode === "date" ? `do ${c.expires}` : "bezterminowa";
  const copy = () => { navigator.clipboard?.writeText(`Kod aktywacyjny: ${c.code}\nAdres serwera: ${window.location.origin}`); toast.success("Skopiowano kod i adres"); };
  return (
    <tr className="border-t border-slate-800" data-testid={`actcode-row-${c.id}`}>
      <td className="py-1 pr-2 font-mono text-cyan-200">{c.code}</td>
      <td className="pr-2">{c.company}</td>
      <td className="pr-2">{c.status === "used" ? `do ${c.license_expires || "bezterminowo"}` : period}</td>
      <td className={`pr-2 ${cls}`}>{label}{c.used_at ? ` ${c.used_at.slice(0, 10)}` : ""}</td>
      <td className="text-right whitespace-nowrap">
        {c.status === "new" && <>
          <button onClick={copy} title="Kopiuj kod" className="text-cyan-300 hover:text-cyan-200 px-1" data-testid={`actcode-copy-${c.id}`}><Copy size={13} /></button>
          <button onClick={() => onCancel(c)} title="Anuluj kod" className="text-red-300 hover:text-red-200 px-1" data-testid={`actcode-cancel-${c.id}`}><Ban size={13} /></button>
        </>}
      </td>
    </tr>
  );
}

export function ActivationCodes({ t, open, onToggle }) {
  const [f, setF] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/license/activation-codes").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    if (open) load();
  }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/license/activation-codes", { ...f, days: Number(f.days) || 0, olt: Number(f.olt) || 0, clients: Number(f.clients) || 0, phone_interval: Number(f.phone_interval) || 0 });
      setLast(data); setF(EMPTY); load();
      toast.success("Kod aktywacyjny utworzony");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const cancel = async (c) => {
    if (!window.confirm(`Anulować kod ${c.code}?`)) return;
    try { await api.delete(`/license/activation-codes/${c.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Section icon={Ticket} testid="settings-sec-actcodes" title={t ? t("Kody aktywacyjne (automatyczna aktywacja)") : "Kody aktywacyjne"} open={open} onToggle={onToggle}>
      <p className="text-[11px] text-slate-400 mb-2">Wpisz dane klienta i okres licencji. Klient po instalacji wpisuje tylko kod i adres tego serwera — licencja przypisze się do jego sprzętu automatycznie. Kod jest jednorazowy i ważny 30 dni.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2" data-testid="actcode-form">
        <Field label="Firma / klient *"><input className={IN} value={f.company} onChange={set("company")} data-testid="actcode-company" /></Field>
        <Field label="NIP"><input className={IN} value={f.client_nip} onChange={set("client_nip")} data-testid="actcode-nip" /></Field>
        <Field label="E-mail"><input className={IN} value={f.client_email} onChange={set("client_email")} data-testid="actcode-email" /></Field>
        <Field label="Telefon"><input className={IN} value={f.client_phone} onChange={set("client_phone")} data-testid="actcode-phone" /></Field>
        <Field label="Adres"><input className={IN} value={f.client_address} onChange={set("client_address")} data-testid="actcode-address" /></Field>
        <Field label="Okres licencji">
          <select className={IN} value={f.mode} onChange={set("mode")} data-testid="actcode-mode">
            <option value="days">liczba dni od aktywacji</option><option value="date">do konkretnej daty</option><option value="unlimited">bezterminowa</option>
          </select>
        </Field>
        {f.mode === "days" && <Field label="Liczba dni"><input type="number" min="1" className={IN} value={f.days} onChange={set("days")} data-testid="actcode-days" /></Field>}
        {f.mode === "date" && <Field label="Ważna do"><input type="date" className={IN} value={f.expires} onChange={set("expires")} data-testid="actcode-expires" /></Field>}
        <Field label="Limit OLT (0 = bez)"><input type="number" min="0" className={IN} value={f.olt} onChange={set("olt")} data-testid="actcode-olt" /></Field>
        <Field label="Limit abonentów (0 = bez)"><input type="number" min="0" className={IN} value={f.clients} onChange={set("clients")} data-testid="actcode-clients" /></Field>
        <Field label="Kontakt z masterem co (dni, 0 = wył.)"><input type="number" min="0" className={IN} value={f.phone_interval} onChange={set("phone_interval")} data-testid="actcode-phone-interval" /></Field>
      </div>
      <button disabled={busy || !f.company.trim()} onClick={create} className="bg-cyan-700 hover:bg-cyan-600 disabled:opacity-40 rounded px-3 py-1 text-xs" data-testid="actcode-create-btn">Utwórz kod aktywacyjny</button>
      {last && (
        <div className="mt-2 p-2 rounded border border-emerald-700 bg-emerald-950/30 text-xs" data-testid="actcode-last">
          Przekaż klientowi: kod <b className="font-mono text-emerald-200 text-sm">{last.code}</b> oraz adres serwera <b className="text-emerald-200">{window.location.origin}</b>
        </div>
      )}
      {rows.length > 0 && (
        <table className="w-full mt-3 text-[11px]" data-testid="actcode-table">
          <thead className="text-slate-500 text-left"><tr><th>Kod</th><th>Klient</th><th>Licencja</th><th>Status</th><th /></tr></thead>
          <tbody>{rows.map((c) => <CodeRow key={c.id} c={c} onCancel={cancel} />)}</tbody>
        </table>
      )}
    </Section>
  );
}
